import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Autocomplete,
  Tooltip,
  MenuItem,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import { Layout } from "../Layout";
import api from "../../services/api";
import { patientService, Patient } from "../../services/patient.service";
import { toLocaleDateString } from "../../utils/dateUtils";

interface SupplyHold {
  id: number;
  patientID: number;
  patientName: string;
  entityName: string;
  externalLookup: string | null;
  holdDate: string;
  releaseDate: string | null;
  holdType: string | null;
  notes: string | null;
}

const PatientSupplyHolds: React.FC = () => {
  const [holds, setHolds] = useState<SupplyHold[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newHold, setNewHold] = useState({
    patientID: "",
    holdDate: "",
    releaseDate: "",
    holdType: "TEMPORARY",
    notes: "",
  });
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [patientOptions, setPatientOptions] = useState<Patient[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [existingHold, setExistingHold] = useState<SupplyHold | null>(null);
  const [filterText, setFilterText] = useState("");

  useEffect(() => {
    loadHolds();
  }, []);

  useEffect(() => {
    const searchPatients = async () => {
      if (searchTerm.length < 2) {
        setPatientOptions([]);
        return;
      }

      try {
        setSearchLoading(true);
        const result = await patientService.searchPatients(searchTerm, 20);
        setPatientOptions(result.patients || []);
      } catch (err) {
        console.error("Patient search error:", err);
        setPatientOptions([]);
      } finally {
        setSearchLoading(false);
      }
    };

    const timeoutId = setTimeout(searchPatients, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const loadHolds = async () => {
    try {
      setLoading(true);
      const response = await api.get("/supply-holds");
      setHolds(response.data.holds || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load supply holds");
    } finally {
      setLoading(false);
    }
  };

  const handlePatientSelect = (patient: Patient | null) => {
    setSelectedPatient(patient);

    if (patient) {
      // Check if patient already has a hold
      const hold = holds.find((h) => h.patientID === patient.id);
      setExistingHold(hold || null);

      if (hold) {
        // Populate with existing hold dates
        setNewHold({
          patientID: patient.id.toString(),
          holdDate: hold.holdDate
            ? new Date(hold.holdDate).toISOString().split("T")[0]
            : "",
          releaseDate: hold.releaseDate
            ? new Date(hold.releaseDate).toISOString().split("T")[0]
            : "",
          holdType: hold.holdType || "TEMPORARY",
          notes: hold.notes || "",
        });
      } else {
        // Reset dates for new hold
        setNewHold({
          patientID: patient.id.toString(),
          holdDate: "",
          releaseDate: "",
          holdType: "TEMPORARY",
          notes: "",
        });
      }
    } else {
      setExistingHold(null);
      setNewHold({
        patientID: "",
        holdDate: "",
        releaseDate: "",
        holdType: "TEMPORARY",
        notes: "",
      });
    }
  };

  const handleSaveHold = async () => {
    if (!selectedPatient) return;

    try {
      const holdData: any = {
        patientID: selectedPatient.id,
        holdDate: new Date(newHold.holdDate).toISOString(),
        releaseDate: newHold.releaseDate
          ? new Date(newHold.releaseDate).toISOString()
          : null,
        holdType: newHold.holdType || "TEMPORARY", // Default to TEMPORARY if not provided
        notes: newHold.notes || null,
      };

      if (existingHold) {
        // Update existing hold
        await api.patch(`/supply-holds/${selectedPatient.id}`, {
          holdDate: holdData.holdDate,
          releaseDate: holdData.releaseDate,
          holdType: holdData.holdType,
          notes: holdData.notes,
        });
      } else {
        // Create new hold
        await api.post("/supply-holds", holdData);
      }

      handleCloseDialog();
      loadHolds();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          `Failed to ${existingHold ? "update" : "create"} hold`,
      );
    }
  };

  const handleRemoveHold = async (patientID: number) => {
    if (!window.confirm("Are you sure you want to remove this supply hold?")) {
      return;
    }

    try {
      await api.delete(`/supply-holds/${patientID}`);
      loadHolds();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to remove hold");
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPatient(null);
    setExistingHold(null);
    setSearchTerm("");
    setPatientOptions([]);
    setNewHold({
      patientID: "",
      holdDate: "",
      releaseDate: "",
      holdType: "TEMPORARY",
      notes: "",
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Indefinite";
    return toLocaleDateString(dateString);
  };

  const getHoldTypeChip = (holdType: string | null) => {
    switch (holdType) {
      case "OPT_OUT":
        return <Chip label='Opt Out' color='error' size='small' />;
      case "PENDING_CONFIRMATION":
        return <Chip label='Pending' color='warning' size='small' />;
      case "DEMO_ACCOUNT":
        return <Chip label='Demo' color='default' size='small' />;
      case "DEXCOM":
        return <Chip label='Dexcom' color='info' size='small' />;
      case "TIME_BASED":
        return <Chip label='Time-Based' color='secondary' size='small' />;
      case "TEMPORARY":
      default:
        return <Chip label='Temporary' color='primary' size='small' />;
    }
  };

  const getHoldTypeLabel = (holdType: string | null): string => {
    switch (holdType) {
      case "OPT_OUT":
        return "Opt Out";
      case "PENDING_CONFIRMATION":
        return "Pending";
      case "DEMO_ACCOUNT":
        return "Demo";
      case "DEXCOM":
        return "Dexcom";
      case "TIME_BASED":
        return "Time-Based";
      case "TEMPORARY":
      default:
        return "Temporary";
    }
  };

  // Filter holds based on search text
  const filteredHolds = holds.filter((hold) => {
    if (!filterText) return true;

    const searchLower = filterText.toLowerCase();
    const holdTypeLabel = getHoldTypeLabel(hold.holdType).toLowerCase();

    return (
      hold.patientID.toString().includes(searchLower) ||
      hold.patientName.toLowerCase().includes(searchLower) ||
      (hold.entityName &&
        hold.entityName.toLowerCase().includes(searchLower)) ||
      (hold.externalLookup &&
        hold.externalLookup.toLowerCase().includes(searchLower)) ||
      holdTypeLabel.includes(searchLower)
    );
  });

  if (loading) {
    return (
      <Layout>
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          minHeight='400px'
        >
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={3}
        >
          <Typography variant='h4'>Patient Supply Holds</Typography>
          <Button
            variant='contained'
            color='primary'
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Add Hold
          </Button>
        </Box>

        {error && (
          <Alert severity='error' onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box mb={2}>
          <TextField
            fullWidth
            label='Search Holds'
            placeholder='Search by patient name, ID, provider, external ID, or hold type'
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            variant='outlined'
            size='small'
          />
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Patient ID</TableCell>
                <TableCell>Patient Name</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>External ID</TableCell>
                <TableCell>Hold Type</TableCell>
                <TableCell>Hold Date</TableCell>
                <TableCell>Release Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align='right'>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHolds.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align='center'>
                    {filterText
                      ? "No holds match your search"
                      : "No supply holds found"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredHolds.map((hold) => (
                  <TableRow key={hold.id}>
                    <TableCell>{hold.patientID}</TableCell>
                    <TableCell>{hold.patientName}</TableCell>
                    <TableCell>{hold.entityName || "N/A"}</TableCell>
                    <TableCell>{hold.externalLookup || "N/A"}</TableCell>
                    <TableCell>{getHoldTypeChip(hold.holdType)}</TableCell>
                    <TableCell>{formatDate(hold.holdDate)}</TableCell>
                    <TableCell>{formatDate(hold.releaseDate)}</TableCell>
                    <TableCell>
                      {!hold.releaseDate ||
                      new Date(hold.releaseDate) > new Date() ? (
                        <Chip label='Active' color='error' size='small' />
                      ) : (
                        <Chip label='Released' color='success' size='small' />
                      )}
                    </TableCell>
                    <TableCell>
                      {hold.notes ? (
                        <Tooltip title={hold.notes} arrow>
                          <InfoIcon
                            fontSize='small'
                            color='action'
                            sx={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align='right'>
                      <IconButton
                        color='error'
                        size='small'
                        onClick={() => handleRemoveHold(hold.patientID)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {existingHold ? "Update Supply Hold" : "Add Supply Hold"}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            fullWidth
            options={patientOptions}
            value={selectedPatient}
            onChange={(_, newValue) => handlePatientSelect(newValue)}
            inputValue={searchTerm}
            onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
            getOptionLabel={(option) =>
              `${option.lastname}, ${option.firstname} (${option.chartID})`
            }
            loading={searchLoading}
            renderOption={(props, option) => {
              const isOnHold = holds.some((h) => h.patientID === option.id);
              return (
                <li {...props}>
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    width='100%'
                    alignItems='center'
                  >
                    <span>{`${option.lastname}, ${option.firstname} (${option.chartID})`}</span>
                    {isOnHold && (
                      <Chip
                        label='On Hold'
                        color='error'
                        size='small'
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin='normal'
                label='Search Patient'
                placeholder='Enter patient name or chart ID'
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              searchTerm.length < 2
                ? "Type at least 2 characters"
                : "No patients found"
            }
          />
          {existingHold && (
            <Alert severity='info' sx={{ mt: 1 }}>
              This patient already has a hold. Updating the fields below will
              modify the existing hold.
            </Alert>
          )}
          <TextField
            fullWidth
            margin='normal'
            label='Hold Type'
            select
            value={newHold.holdType}
            onChange={(e) =>
              setNewHold({ ...newHold, holdType: e.target.value })
            }
            required
          >
            <MenuItem value='TEMPORARY'>Temporary</MenuItem>
            <MenuItem value='OPT_OUT'>Opt Out</MenuItem>
            <MenuItem value='PENDING_CONFIRMATION'>
              Pending Confirmation
            </MenuItem>
            <MenuItem value='DEMO_ACCOUNT'>Demo Account</MenuItem>
            <MenuItem value='DEXCOM'>Dexcom</MenuItem>
            <MenuItem value='TIME_BASED'>Time-Based</MenuItem>
          </TextField>
          <TextField
            fullWidth
            margin='normal'
            label='Hold Date'
            type='date'
            InputLabelProps={{ shrink: true }}
            value={newHold.holdDate}
            onChange={(e) =>
              setNewHold({ ...newHold, holdDate: e.target.value })
            }
            required
          />
          <TextField
            fullWidth
            margin='normal'
            label='Release Date (Optional)'
            type='date'
            InputLabelProps={{ shrink: true }}
            value={newHold.releaseDate}
            onChange={(e) =>
              setNewHold({ ...newHold, releaseDate: e.target.value })
            }
            helperText='Leave empty for indefinite hold'
          />
          <TextField
            fullWidth
            margin='normal'
            label='Notes (Optional)'
            multiline
            rows={3}
            value={newHold.notes}
            onChange={(e) => setNewHold({ ...newHold, notes: e.target.value })}
            inputProps={{ maxLength: 250 }}
            helperText={`${newHold.notes.length}/250 characters`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveHold}
            variant='contained'
            color='primary'
            disabled={!selectedPatient || !newHold.holdDate}
          >
            {existingHold ? "Update Hold" : "Add Hold"}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default PatientSupplyHolds;
